<template>
    <main-layout>
        <div id="roles" class="page animated fadeIn">
            <v-container fluid>
                <h1>Roles</h1>
                <v-data-iterator
                        :items="getRoles"
                        :items-per-page.sync="itemsPerPage"
                        :page="page"
                        :search="search"
                        :sort-by="sortBy.toLowerCase()"
                        :sort-desc="sortDesc"
                        hide-default-footer
                >
                    <template v-slot:header>
                        <v-toolbar
                                dark
                                color="transparent"
                                flat
                                height="64px"
                        >
                            <v-row justify="space-between" align="center">
                                <v-col cols="12" lg="2" class="pa-0">
                                    <v-text-field
                                            v-model="search"
                                            clearable
                                            flat
                                            hide-details
                                            append-icon="mdi-magnify"
                                            label="Search"
                                    ></v-text-field>
                                </v-col>
                                <v-spacer></v-spacer>
                                <v-col cols="12" lg="2" class="text-center text-lg-right pa-lg-0">
                                    <form-dialog
                                            :form-title="formTitle"
                                            activator-text="New Role"
                                            :dialog="dialog"
                                            :success-message="successMessage"
                                            :form-loading="formLoading"
                                            :data-sent="dataSent"
                                            :valid="valid"
                                            @dialogOpen="dialog = true"
                                            @dialogClose="handleDialogClose"
                                            @formSubmit="handleRoleSubmit"
                                    >
                                        <v-row>
                                            <v-col cols="12">
                                                <v-text-field label="Name" :rules="rules.name" v-model="editRole.name" required>
                                                </v-text-field>
                                            </v-col>
                                            <v-col cols="12">
                                                <h2>Permissions</h2>
                                                <v-expansion-panels>
                                                    <v-expansion-panel
                                                            v-for="app in apps"
                                                            :key="app.id"
                                                    >
                                                        <v-expansion-panel-header>{{app.name}}</v-expansion-panel-header>
                                                        <v-expansion-panel-content>
                                                            <v-row>
                                                                <v-col cols="12" lg="4"
                                                                       :key="permission.id"
                                                                       v-for="permission in app.Permissions"
                                                                >
                                                                    <v-checkbox v-model="permissions"
                                                                                :label="permission.name"
                                                                                :value="permission.id">
                                                                    </v-checkbox>
                                                                </v-col>
                                                            </v-row>
                                                        </v-expansion-panel-content>
                                                    </v-expansion-panel>
                                                </v-expansion-panels>
                                            </v-col>
                                        </v-row>
                                    </form-dialog>
                                </v-col>
                            </v-row>
                        </v-toolbar>
                    </template>
                    <template v-slot:default="props">
                        <v-row>
                            <Role v-for="role in props.items"
                                  :key="role.id" :role="role" @edit="handleEditClick"></Role>
                        </v-row>
                    </template>
                    <template v-slot:footer>
                        <v-row class="mt-2" align="center" justify="center">
                            <span class="grey--text">Items per page</span>
                            <v-menu offset-y>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                            dark
                                            text
                                            color="primary"
                                            class="ml-2"
                                            v-bind="attrs"
                                            v-on="on"
                                    >
                                        {{ itemsPerPage }}
                                        <v-icon>mdi-chevron-down</v-icon>
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item
                                            v-for="(number, index) in itemsPerPageArray"
                                            :key="index"
                                            @click="updateItemsPerPage(number)"
                                    >
                                        <v-list-item-title>{{ number }}</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                            <v-spacer></v-spacer>
                            <span class="mr-4 grey--text">
                                Page {{ page }} of {{ numberOfPages }}
                            </span>
                            <v-btn
                                    fab
                                    small
                                    dark
                                    color="primary"
                                    class="mr-1"
                                    @click="formerPage"
                            >
                                <v-icon>mdi-chevron-left</v-icon>
                            </v-btn>
                            <v-btn
                                    fab
                                    dark
                                    small
                                    color="primary"
                                    class="ml-1"
                                    @click="nextPage"
                            >
                                <v-icon>mdi-chevron-right</v-icon>
                            </v-btn>
                        </v-row>
                    </template>
                </v-data-iterator>
            </v-container>
        </div>
    </main-layout>
</template>

<script>
import { mapGetters } from 'vuex';
import store from '../../store';
import FormDialog from '../common/FormDialog.vue';
import Role from '../common/Role.vue';
import MainLayout from '../layouts/MainLayout.vue';

export default {
  data() {
    return {
      valid: true,
      dialog: false,
      newRole: {
        name: '',
        permissions: [],
      },
      editRole: {
        name: '',
        permissions: [],
      },
      permissions: [],
      editedIndex: -1,
      itemsPerPageArray: [4, 8, 12],
      search: '',
      filter: {},
      sortDesc: false,
      page: 1,
      itemsPerPage: 8,
      sortBy: 'name',
      apps: [],
      rules: {
        name: [
          (value) => Boolean(value) || 'Required.',
          (value) => (value || '').length <= 255 || 'Max 255 characters',
        ],
      },
      formLoading: false,
      dataSent: false,
      successMessage: 'Data has been saved!',
    };
  },
  computed: {
    numberOfPages() {
      return Math.ceil(this.getRoles.length / this.itemsPerPage);
    },
    formTitle() {
      return this.editedIndex > -1 ? 'Edit Role' : 'New Role';
    },
    ...mapGetters('applications', ['getApps']),
    ...mapGetters('roles', ['getRoles']),
  },
  methods: {
    nextPage() {
      if (this.page + 1 <= this.numberOfPages) {
        this.page += 1;
      }
    },
    formerPage() {
      if (this.page - 1 >= 1) {
        this.page -= 1;
      }
    },
    updateItemsPerPage(number) {
      this.itemsPerPage = number;
    },
    async handleRoleSubmit(form) {
      if (!form.validate()) {
        return;
      }
      this.editRole.permissions = this.permissions;
      this.formLoading = true;
      if (this.editedIndex > -1) {
        await store.dispatch('roles/updateRole', this.editRole);
      } else {
        await store.dispatch('roles/createRole', this.editRole);
      }
      this.formLoading = false;
      this.dataSent = true;

      setTimeout(() => {
        this.handleDialogClose(form);
      }, 2000);
    },
    handleEditClick(item) {
      this.editedIndex = this.getRoles.indexOf(item);
      this.editRole = { ...item };
      this.permissions = item.Permissions.reduce((array, perm) => {
        array.push(perm.id);

        return array;
      }, []);
      this.dialog = true;
    },
    handleDialogClose(form) {
      this.dialog = false;
      this.dataSent = false;
      this.$nextTick(() => {
        form.resetValidation();
        this.editRole = { ...this.newRole };
        this.permissions = [];
        this.editedIndex = -1;
      });
    },
  },
  async mounted() {
    await store.dispatch('roles/saveRolesStore');
    await store.dispatch('applications/saveAppsStore');
    this.apps = this.getApps;
  },
  components: {
    FormDialog,
    MainLayout,
    Role,
  },
  name: 'Roles',
};
</script>

<style lang="sass">
</style>
